import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, ListItemText, Menu, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import { EditOutlined as EditIcon, FileDownloadOutlined as ExportIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { barButtonSx } from '../monthly/MultiSelectFilter';
import { cardSx, GREEN, GREEN_DARK, INK, LINE, MUTED } from '../home/homeTheme';
import { fetchMyRevenues, fetchRevenues, fmtPeriod, saveRevenues } from '../../utils/projectsService';

const FIELDS = ['ricavi_anno_prec', 'ricavi_anno_corrente', 'ricavi_anno_succ', 'ricavi_actual', 'canone_mensile'];
const EMPTY_FORM = Object.fromEntries(FIELDS.map((f) => [f, '']));
const toForm = (c) => Object.fromEntries(FIELDS.map((f) => [f, c?.[f] != null ? String(c[f]) : '']));
const euro = (v) => (v == null || v === '' ? null : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(v)));
const fmtStamp = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);

// Stesse colonne dell'export della pagina classica
function exportRow(c) {
  const year = new Date().getFullYear();
  return {
    Customer: c?.cliente || '',
    'Project name': c?.servizio || '',
    'Project Code': c?.project_code || '',
    'Yubiq Code': c?.yubiq_code || '',
    'Service Type': c?.tipologia_servizio || '',
    'Surplus/Deficit vs Actual Revenues': c?.ricavi_actual ?? '',
    [`Revenue ${year - 1} (year-1)`]: c?.ricavi_anno_prec ?? '',
    [`Revenue ${year} (current year)`]: c?.ricavi_anno_corrente ?? '',
    [`Revenue ${year + 1} (year+1)`]: c?.ricavi_anno_succ ?? '',
    'Current Monthly Fee': c?.canone_mensile ?? '',
  };
}
function downloadXlsx(rows, fileName) {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Revenues');
  saveAs(new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), fileName);
}

/** Card di un valore economico: in lettura l'importo, in modifica il campo. */
function AmountCard({ label, caption, field, value, editing, form, onChange, highlight, signed }) {
  const n = value == null ? null : Number(value);
  const color = signed && n ? (n > 0 ? GREEN_DARK : 'var(--rt-danger)') : INK;
  return (
    <Box sx={{ ...cardSx, p: 2, ...(highlight ? { borderColor: GREEN } : {}) }}>
      <Typography sx={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>{label}</Typography>
      {editing ? (
        <TextField
          size="small"
          type="number"
          fullWidth
          value={form[field]}
          onChange={(e) => onChange(field, e.target.value)}
          inputProps={{ step: 0.01 }}
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
          sx={{ mt: 1 }}
        />
      ) : (
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: n == null ? MUTED : color, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
          {n == null ? '—' : `${signed && n > 0 ? '+' : ''}${euro(n)}`}
        </Typography>
      )}
      {caption && <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>{caption}</Typography>}
    </Box>
  );
}

function Detail({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: children ? INK : MUTED, mt: 0.25, wordBreak: 'break-word' }}>{children || 'Not set'}</Typography>
    </Box>
  );
}

/**
 * Scheda Revenues: ricavi per anno, scostamento dal consuntivo e (per i Monthly Rate) il canone,
 * modificabili nella pagina; export del progetto o di tutti i progetti dell'utente.
 */
export default function ProjectRevenues({ project, userId, canEdit, onDirtyChange }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchRevenues(project.id)
      .then((c) => !cancelled && setContract(c))
      .catch((e) => !cancelled && setError(`Unable to load revenues: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  const dirty = editing && JSON.stringify(form) !== JSON.stringify(toForm(contract));
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const startEdit = () => {
    setForm(toForm(contract));
    setEditing(true);
  };
  const change = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveRevenues(project.id, form, userId);
      setContract(await fetchRevenues(project.id));
      setEditing(false);
      setSnack('Revenues saved');
    } catch (e) {
      setError(`Not saved: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const exportProject = () => {
    setExportAnchor(null);
    downloadXlsx(
      [
        exportRow({
          ...contract,
          cliente: contract?.cliente || project.customer?.name || '',
          servizio: contract?.servizio || project.name || '',
          project_code: contract?.project_code || project.project_code || '',
          yubiq_code: contract?.yubiq_code ?? project.yubiq_code ?? '',
          tipologia_servizio: contract?.tipologia_servizio ?? project.tipologia_servizio ?? '',
        }),
      ],
      `revenues_${project.name || 'project'}.xlsx`,
    );
  };
  const exportAll = async () => {
    setExportAnchor(null);
    try {
      const all = await fetchMyRevenues(userId);
      if (!all.length) setSnack('No revenues to export');
      else downloadXlsx(all.map(exportRow), 'revenues_export.xlsx');
    } catch (e) {
      setSnack(`Export failed: ${e.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  const year = new Date().getFullYear();
  const monthlyRate = (contract?.tipologia_servizio || project.tipologia_servizio) === 'Monthly Rate';
  // Valore mostrato (0 compreso); vuoto = non inserito
  const shownValue = (field) => {
    const v = editing ? form[field] : contract?.[field];
    return v === '' || v == null ? null : v;
  };
  const cardProps = { editing, form, onChange: change };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13.5, color: MUTED, flex: 1 }}>
          {contract ? 'Revenues of the project, in euro.' : 'No revenues have been entered for this project yet.'}
        </Typography>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={(e) => setExportAnchor(e.currentTarget)} sx={barButtonSx}>
          Export
        </Button>
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={exportProject} disabled={!contract}>
            <ListItemText primary="This project" secondary="Excel file with this project" />
          </MenuItem>
          <MenuItem onClick={exportAll} disabled={!userId}>
            <ListItemText primary="All my projects" secondary="Excel file with every project you manage" />
          </MenuItem>
        </Menu>
        {canEdit && !editing && (
          <Button variant="contained" color="success" startIcon={<EditIcon />} onClick={startEdit} sx={{ ...barButtonSx, color: '#fff', bgcolor: GREEN, boxShadow: 'none', fontWeight: 700 }}>
            {contract ? 'Edit revenues' : 'Add revenues'}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: `repeat(${monthlyRate ? 5 : 4}, minmax(0, 1fr))` }, gap: 2 }}>
        <AmountCard label={`Revenue ${year - 1}`} caption="Previous year" field="ricavi_anno_prec" value={shownValue('ricavi_anno_prec')} {...cardProps} />
        <AmountCard label={`Revenue ${year}`} caption="Current year" field="ricavi_anno_corrente" value={shownValue('ricavi_anno_corrente')} highlight {...cardProps} />
        <AmountCard label={`Revenue ${year + 1}`} caption="Next year" field="ricavi_anno_succ" value={shownValue('ricavi_anno_succ')} {...cardProps} />
        <AmountCard label="Surplus / deficit" caption="Compared with actual revenues" field="ricavi_actual" value={shownValue('ricavi_actual')} signed {...cardProps} />
        {monthlyRate && <AmountCard label="Current monthly fee" caption="Monthly Rate service" field="canone_mensile" value={shownValue('canone_mensile')} {...cardProps} />}
      </Box>

      <Box sx={{ ...cardSx, p: 2.25 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, mb: 1.75 }}>Customer and project</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, rowGap: 1.75, columnGap: 2 }}>
          <Detail label="Customer">{contract?.cliente || project.customer?.name}</Detail>
          <Detail label="Project">{contract?.servizio || project.name}</Detail>
          <Detail label="Project code">{project.project_code}</Detail>
          <Detail label="Yubiq code">{contract?.yubiq_code ?? project.yubiq_code}</Detail>
          <Detail label="Service type">{contract?.tipologia_servizio || project.tipologia_servizio}</Detail>
          <Detail label="Revenue period">{fmtPeriod(project)}</Detail>
        </Box>
      </Box>

      {contract && (
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          {[
            fmtStamp(contract.created_at) && `Created ${fmtStamp(contract.created_at)}${contract.created_by_username ? ` by ${contract.created_by_username}` : ''}`,
            fmtStamp(contract.updated_at) && `last updated ${fmtStamp(contract.updated_at)}${contract.updated_by_username ? ` by ${contract.updated_by_username}` : ''}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}

      {/* Barra di salvataggio durante la modifica */}
      {editing && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 3,
            bgcolor: 'var(--rt-paper)',
            border: `1px solid ${LINE}`,
            boxShadow: '0 12px 32px -12px rgba(15,23,18,0.35)',
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: INK, flex: 1 }}>{dirty ? 'Changes not saved' : 'Editing revenues'}</Typography>
          <Button color="inherit" onClick={() => setEditing(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={save} disabled={saving || !dirty} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 110, bgcolor: GREEN }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      )}

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
